import { COMMON_CONSTANTS } from 'src/app/common/constants/common-constants';
import { UserIdentityDocument } from 'src/app/common/models/chatbotexpand.model';
import { firstValueFrom } from 'rxjs';
import { ChatListDocument, ChatHistoryDocument, ChatAnswerDocument, SaveChatResponseRequest } from '../../../../common/models/chatbotexpand.model';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

/**
 * ChatHistoryHelper
 * 
 * Static utility methods for chat history parsing and formatting operations.
 * 
 * Methods:
 * 1. parseChatResponse() - Parse chat response into structured objects
 * 2. generateGuid() - Generate unique identifier for chat sessions
 */

export class ChatHistoryHelper {
  
  /**
   * Parse chat response array into structured response objects
   */
  static parseChatResponse(ChatResponse: any): {
    AgentPayload?: any;
    GetMcode?: any;
    getSnapshotConfiguration?: any;
    GetCustomSnapshotWidget?: any;
    GetQuery?: any;
    getSnapshotOutput?: any;
    getSnapshotLoadData?: any;
    getConfiguration?: any;
    getSnapshotInsights?: any;
    getSnapshotInsightsTrend?: any;
    getInsights?: any;
    getQnA?: any;
    trendline_response?: any;
    response_payload_trendline?: any;
    customization_Properties_trendline?: any;
    response_payload?: any;
    member_responses?: any;
  } {
    const parsedResponses: Record<string, any> = {};

    let targetPayload = (Array.isArray(ChatResponse) && ChatResponse.length === 1) ? ChatResponse[0] : ChatResponse;

    if (typeof targetPayload === 'object' && targetPayload !== null &&
      (targetPayload.snapshot || targetPayload.brand_analysis || targetPayload.brandanalysis || targetPayload.crosstab || targetPayload.final_mcode !== undefined)) {
      const finalMcode = targetPayload.final_mcode;

      let activeMod: any;
      if (finalMcode === 1) activeMod = targetPayload.snapshot;
      else if (finalMcode === 2) activeMod = targetPayload.brand_analysis || targetPayload.brandanalysis;
      else if (finalMcode === 3) activeMod = targetPayload.crosstab;

      if (!activeMod) {
        activeMod = targetPayload.snapshot || targetPayload.brand_analysis || targetPayload.brandanalysis || targetPayload.crosstab || {};
      }
      if (activeMod.getSnapshotOutput) {
        if (typeof activeMod.getSnapshotOutput === 'object' && activeMod.getSnapshotOutput !== null) {
        } else if (typeof activeMod.getSnapshotOutput === 'string') {
        }
      }

      const tryParse = (val: any) => {
        if (typeof val === 'string') {
          try {
            const trimmed = val.trim();
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
              return JSON.parse(trimmed);
            }
          } catch (e) { }
        }
        return val;
      };

      let parsedAgentPayload = tryParse(activeMod.AgentPayload);
      if (!parsedAgentPayload) {
        parsedAgentPayload = {
          mcode: activeMod.mcode,
          promptData: {
            Primary_Brands: activeMod.Primary_Brands,
            Primary_Metrics: activeMod.Primary_Metrics,
            Markets: activeMod.Markets,
            Time_Period: activeMod.Time_Period,
            Filter_Audience: activeMod.Filter_Audience,
            Metric_Filters: activeMod.Metric_Filters,
            snapshot_type: activeMod.snapshot_type,
            Snapshot_Name: activeMod.Snapshot_Name,
            Section: activeMod.Section,
            Widget_ID: activeMod.Widget_ID,
            Widget_Name: activeMod.Widget_Name,
            requested_metrics: activeMod.requested_metrics,
            weight: activeMod.weight || activeMod.Weight || tryParse(activeMod.getConfiguration)?.brand_analysis_payload?.SelectedWeight || tryParse(activeMod.getConfiguration)?.crosstab_payload?.SelectedWeight || activeMod.SelectedWeight,
            Weight: activeMod.Weight || activeMod.weight || tryParse(activeMod.getConfiguration)?.brand_analysis_payload?.SelectedWeight || tryParse(activeMod.getConfiguration)?.crosstab_payload?.SelectedWeight || activeMod.SelectedWeight,
            row: activeMod.row || activeMod.Row,
            column: activeMod.column || activeMod.Column,
            row_nesting: activeMod.row_nesting || activeMod.Row_Nesting,
            column_nesting: activeMod.column_nesting || activeMod.Column_Nesting,
            filters: activeMod.filters || activeMod.Filters
          }
        };
      }

      return {
        AgentPayload: parsedAgentPayload,
        GetMcode: tryParse(activeMod.GetMcode) || tryParse(activeMod.mcode) || tryParse(ChatResponse.GetMcode) || tryParse(ChatResponse.mcode),
        getSnapshotConfiguration: tryParse(activeMod.getSnapshotConfiguration) || tryParse(activeMod.getConfiguration) || tryParse(ChatResponse.getSnapshotConfiguration) || tryParse(ChatResponse.getConfiguration),
        GetCustomSnapshotWidget: tryParse(activeMod.GetCustomSnapshotWidget) || tryParse(ChatResponse.GetCustomSnapshotWidget),
        GetQuery: tryParse(activeMod.GetQuery) || tryParse(ChatResponse.GetQuery),
        getSnapshotOutput: tryParse(activeMod.getSnapshotOutput) || tryParse(activeMod.getConfiguration) || tryParse(ChatResponse.getSnapshotOutput),
        getSnapshotLoadData: tryParse(activeMod.getSnapshotLoadData) || tryParse(ChatResponse.getSnapshotLoadData),
        getConfiguration: tryParse(activeMod.getConfiguration) || tryParse(ChatResponse.getConfiguration),
        getSnapshotInsights: tryParse(activeMod.getSnapshotInsights) || tryParse(activeMod.getInsights) || tryParse(targetPayload.getSnapshotInsights),
        getSnapshotInsightsTrend: tryParse(activeMod.getSnapshotInsights_Trend) || tryParse(targetPayload.getSnapshotInsights_Trend),
        getInsights: tryParse(activeMod.getInsights) || tryParse(targetPayload.getInsights),
        getQnA: tryParse(activeMod.getQnA) || tryParse(targetPayload.getQnA),
        trendline_response: tryParse(targetPayload.trendline_response) || tryParse(activeMod.trendline_response),
        response_payload_trendline: tryParse(targetPayload.response_payload_trendline) || tryParse(activeMod.response_payload_trendline),
        customization_Properties_trendline: tryParse(targetPayload.customization_Properties_trendline) || tryParse(activeMod.customization_Properties_trendline),
        response_payload: tryParse(targetPayload.response_payload) || tryParse(activeMod.response_payload),
        member_responses: targetPayload // Inject the whole object as member_responses so toggles work
      };
    }

    ChatResponse.forEach((item: any, index: any) => {
      if (typeof item !== 'object' || item === null) return;

      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (typeof value === 'string') {
          try {
            // Only try to parse if it looks like JSON (starts with { or [)
            const trimmedValue = value.trim();
            if ((trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) ||
              (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))) {
              parsedResponses[key] = JSON.parse(value);
            } else {
              parsedResponses[key] = value;
            }
          } catch (e) {
            parsedResponses[key] = value; // Keep original value if parsing fails
          }
        } else {
          parsedResponses[key] = value;
        }
      });
    });

    return {
      AgentPayload: parsedResponses['AgentPayload'],
      GetMcode: parsedResponses['GetMcode'],
      getSnapshotConfiguration: parsedResponses['getSnapshotConfiguration'],
      GetCustomSnapshotWidget: parsedResponses['GetCustomSnapshotWidget'],
      GetQuery: parsedResponses['GetQuery'],
      getSnapshotOutput: parsedResponses['getSnapshotOutput'],
      getSnapshotLoadData: parsedResponses['getSnapshotLoadData'],
      getConfiguration: parsedResponses['getConfiguration'],
      getSnapshotInsights: parsedResponses['getSnapshotInsights'],
      getSnapshotInsightsTrend: parsedResponses['getSnapshotInsights_Trend'],
      getInsights: parsedResponses['getInsights'],
      getQnA: parsedResponses['getQnA'],
      trendline_response: parsedResponses['trendline_response'],
      response_payload_trendline: parsedResponses['response_payload_trendline'],
      customization_Properties_trendline: parsedResponses['customization_Properties_trendline'],
      response_payload: parsedResponses['response_payload'],
      member_responses: parsedResponses['member_responses']
    };
  }

  /**
   * Generate unique identifier for chat sessions
   */
  static generateGuid(): string {
    return uuidv4();
  }

  /**
   * Helper method to format chat response entry for storage
   * Used for consistent logging and storage formatting
   */
  static formatChatResponseEntry(key: string, value: any): { [key: string]: string } {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    return { [key]: stringValue };
  }

  /**
   * Helper method to sort chat history by message index
   * Returns sorted array without modifying original
   */
  static sortChatHistoryByIndex(history: any[]): any[] {
    if (!history || history.length === 0) return [];
    
    return [...history].sort((a, b) => {
      const indexA = a.MessageIndex !== undefined ? a.MessageIndex : 0;
      const indexB = b.MessageIndex !== undefined ? b.MessageIndex : 0;
      return indexA - indexB;
    });
  }

  /**
   * Helper method to filter chat list by search tokens
   * Returns filtered chats based on title matching and optionally nested question matching
   */
  static filterChatsBySearch(
    allChats: any[],
    searchText: string,
    nestedSearchText: string = ''
  ): any[] {
    const searchTokens = searchText.toLowerCase().trim().split(/\s+/).filter((token: any) => token.length > 0);
    const nestedTokens = nestedSearchText.toLowerCase().trim().split(/\s+/).filter((token: any) => token.length > 0);
    
    if (searchTokens.length === 0 && nestedTokens.length === 0) {
      return allChats;
    }

    // Step 1: Filter by Primary Search (Chat title)
    let filtered = allChats;
    if (searchTokens.length > 0) {
      filtered = filtered.filter((chat: any) => {
        const title = (chat.ChatTitle || '').toLowerCase();
        return searchTokens.every((token: any) => title.includes(token));
      });
    }

    // Step 2: Nested Filter (Search within history questions of the already filtered chats)
    if (nestedTokens.length > 0) {
      filtered = filtered.map((chat: any) => {
        // Deep clone to avoid modifying original list in memory
        const matchedQuestions = (chat.ChatHistory || []).filter((h: any) => {
          const questionText = (h.Question || '').toLowerCase();
          return nestedTokens.every((token: any) => questionText.includes(token));
        });

        if (matchedQuestions.length > 0) {
          return { ...chat, ChatHistory: matchedQuestions };
        }
        return null;
      }).filter((chat: any) => chat !== null);
    }

    return filtered;
  }

  /**
   * Helper method to map chat list to accordion format grouped by month
   * Returns structured accordion data for UI display
   */
  static mapChatsToAccordions(chats: any[]): any[] {
    const resultGroups: any[] = [];
    const sortedChats = ChatHistoryHelper.sortChatsByDate(chats);
    sortedChats.forEach((chat: any) => {
      const firstQuestion = (chat.ChatHistory || []).find((h: any) => h.MessageIndex === 0);
      const history = (chat.ChatHistory || []).map((message: any) => ({
        ...message,
        AnswerPreview: ChatHistoryHelper.createAnswerPreview(message.QuestionAnswer)
      }));
      const headerTitle = firstQuestion ? firstQuestion.Question : chat.ChatTitle;

      const date = chat.CreatedAt ? new Date(chat.CreatedAt) : new Date();
      const year = date.getFullYear();
      const currentYear = new Date().getFullYear();
      
      let monthKey = date.toLocaleString('default', { month: 'long' });
      
      if (year !== currentYear) {
         monthKey += ` ${year}`;
      }

      let group = resultGroups.find((g: any) => g.month === monthKey);
      if (!group) {
        group = { month: monthKey, isExpanded: true, chats: [] };
        resultGroups.push(group);
      }

      group.chats.push({
        mainQuestion: headerTitle,
        mainQuestionMessageId: firstQuestion?.MessageId || null,
        chatId: chat.ChatId,
        questions: history,
        isExpanded: false
      });
    });

    return resultGroups;
  }

  static createAnswerPreview(questionAnswer: string | undefined): string {
    if (!questionAnswer) return '';

    return questionAnswer
      .replace(/\[\[\[SPLIT\]\]\]/g, ' ')
      .replace(/\\\\n/g, ' ')
      .replace(/\\n/g, ' ')
      .replace(/\/n/g, ' ')
      .replace(/\\\\r/g, ' ')
      .replace(/\\r/g, ' ')
      .replace(/\\t/g, ' ')
      .replace(/\n/g, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/[#*_`~>\[\]()|\\-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Helper method to create chat preview block
   * Returns formatted chat preview object for UI
   */
  static createChatPreviewBlock(
    message: any,
    defaultQuestionAnswer: string,
    defaultWeight: string = 'Total Population',
    defaultBase: string = 'Total Base',
    selectedWeight: string = 'Weight'
  ): any {
    return {
      question: message.Question || '',
      response: message.QuestionAnswer || '',
      messageId: message.MessageId,
      isStarred: message.IsStarred || false,
      isLiked: message.IsLiked ?? null,
      feedbackSaved: (message.IsLiked === true || message.IsLiked === false),
      showLikeInput: false,
      showDislikeInput: false,
      showDeleteConfirm: false,
      likeReason: message.LikeReason || '',
      dislikeReason: message.DislikeReason || '',
      QuestionAnswer: message.QuestionAnswer || defaultQuestionAnswer,
      fullAnswer: null,
      parsedData: null,
      mcode: 0,
      analyticalData: null,
      // Isolated UI State Flags
      isExportspopup: false,
      sampleSizeDropdown: false,
      confidenceLevelDropDown: false,
      referenceLevelDropDown: false,
      baseDropDown: false,
      weightDropDown: false,
      isReference: false,
      // Isolated Analytical Parameters
      isSig: true,
      isIndex: false,
      DefaultSampleSize: 'None',
      SignificanceVsReference: '',
      DefaultConfidenceLevel: '95%',
      DefaultReferenceLevel: 'None',
      selectedWeight: selectedWeight,
      DefaultWeight: defaultWeight,
      DefaultBase: defaultBase,
      isTotalBase: true,
      isAnswerBase: true,
      is_sigtesting: true,
      is_Time_Period_Present: true,
      isTranspose: false,
      isHideNA: false,
      isTotal: false,
      dataDropDown: false,
      DefaultDataDropDown: 'Data',
      isTableClick: true,
      isVerticalStackChartClick: false,
      isVerticalBarChartClick: false,
      isVerticalGroupBarChartClick: false,
      isLineChartClick: false,
      isChartView: true,
      disableStackChart: false,
      disableLineChart: false,
      showStackTooltip: false,
      showLineTooltip: false,
      showBarTooltip: false,
      showMainTooltip: false,
      StackFlag: false,
      displayTable: true,
      isTableClosing: false,
      isChartClosing: false,
      // Isolated Chart Objects
      StackChart: { title: '', Data: [], Properties: [] },
      LineChart: { title: '', Data: [], Properties: [] },
      BarChart: { title: '', Data: [], Properties: [] },
      // Isolated Data Storage
      OutputdataObject: { Row: [], Column: [], RowNesting: [], ColNesting: [], Filter: [], CustomGroup: [], CustomCalculation: [] },
      CrossTabOutPutData: [],
      CrossTabOutPutEntireData: []
    };
  }

  /**
   * Helper method to sort chats by creation date
   * Returns sorted array with most recent first
   */
  static sortChatsByDate(chats: any[]): any[] {
    return [...chats].sort((a, b) => {
      const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
      const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
  }

  static buildHistoricalWidgetData(comp: any, data: any, analytical?: any): any {
    if (!data || !data.Data) {
      console.warn('[buildHistoricalWidgetData] ⚠️ Missing Data property, returning raw data.');
      return data;
    }

    try {
      const opData = typeof data.Data === 'string' ? JSON.parse(data.Data) : data.Data;

      if (Array.isArray(opData) && opData.length > 0) {
        const uniqueWidgets = _.uniqBy(opData, 'widgetNumber');
        let selectedWidget: any = null;

        const snapConfigStr = analytical?.member_responses?.snapshot?.getSnapshotConfiguration || analytical?.getSnapshotConfiguration || data.getSnapshotConfiguration;
        let snapConfig: any = null;
        if (snapConfigStr) {
          try { snapConfig = typeof snapConfigStr === 'string' ? JSON.parse(snapConfigStr) : snapConfigStr; } catch (e) { }
        }

        const targetWidgetID = snapConfig?.Widget_ID || snapConfig?.widget_id
          || data.Widget_ID || data.widget_id || analytical?.Widget_ID || analytical?.widget_id
          || analytical?.getSnapshotOutput?.Widget_ID || analytical?.getSnapshotOutput?.widget_id
          || analytical?.member_responses?.snapshot?.Widget_ID || analytical?.member_responses?.snapshot?.widget_id;
        const targetWidgetName = snapConfig?.Widget_Name || snapConfig?.widget_name
          || data.Widget_Name || data.widget_name || analytical?.Widget_Name || analytical?.widget_name
          || analytical?.member_responses?.snapshot?.Widget_Name || analytical?.member_responses?.snapshot?.widget_name;

        if (targetWidgetName) {
          selectedWidget = uniqueWidgets.find((widget: any) =>
            String(widget.widgetName || '').trim().toLowerCase() === String(targetWidgetName).trim().toLowerCase()
          );
        }

        if (!selectedWidget && targetWidgetID) {
          selectedWidget = uniqueWidgets.find((widget: any) => String(widget.widgetNumber) === String(targetWidgetID));
        }

        if (!selectedWidget) {
          selectedWidget = uniqueWidgets[0];
        }

        const widgetFilteredData = opData.filter((item: any) => item.widgetNumber === selectedWidget.widgetNumber);
        const fallbackBase = widgetFilteredData.find((r: any) => r.answerUnweightedBase != null)?.answerUnweightedBase || 999;

        const uniqueMetrics = _.uniqBy(widgetFilteredData, 'rowChild').map((item: any) => ({
          option: item.rowChild,
          selectOrder: 0,
          typeofmetric: 'METRIC',
          metric_group: '',
          data: widgetFilteredData
            .filter((row: any) => row.rowChild === item.rowChild)
            .map((row: any) => {
              const colParts = (row.colChild || '').split('|');
              const specificCol = colParts.pop() || row.colChild;
              const parentLabel = colParts.pop() || 'Market';
              const resolvedColChild = `${parentLabel}|${specificCol}`;

              return {
                data: row.answerWeightedShare,
                data2: row.answerWeightedShareIndex,
                lvl1Child: row.Level1ColChild,
                colChild: resolvedColChild,
                rowChild: row.rowChild,
                totalUnweightedBase: row.totalUnweightedBase,
                totalUnweightedShare: row.totalUnweightedShare,
                totalWeightedBase: row.totalWeightedBase,
                totalWeightedShare: row.totalWeightedShare,
                totalWeightedShareIndex: row.totalWeightedShareIndex,
                answerUnweightedBase: row.answerUnweightedBase,
                answerUnweightedShare: row.answerUnweightedShare,
                answerWeightedBase: row.answerWeightedBase,
                answerWeightedShare: row.answerWeightedShare,
                answerWeightedShareIndex: row.answerWeightedShareIndex,
                changeVsPY_Answer: row.changeVsPY_Answer,
                changeVsPY_Total: row.changeVsPY_Total,
                Level1ColResponseCode: row.Level1ColResponseCode,
                Level1NestColVariableCode: row.Level1NestColVariableCode,
                CalculationType: row.CalculationType || 'share',
                CalculationTypeIndex: row.CalculationTypeIndex || 'index',
                baseValueforLSorIs: row.answerUnweightedBase ?? fallbackBase,
                changeVsPY: row.changeVsPY_Answer,
                Significance_Value: row.Significance_Answer
              };
            })
        }));

        uniqueMetrics.sort((a: any, b: any) => {
          const valA = a.data?.[0]?.data ?? 0;
          const valB = b.data?.[0]?.data ?? 0;
          return valB - valA;
        });

        const isBubbleChart = selectedWidget.widgetChartType?.toLowerCase().includes('bubble');

        const builtData = {
          widgetchart_name: selectedWidget.widgetChartType,
          widget_name: selectedWidget.widgetName,
          previewChartPostion: selectedWidget.widgetNumber,
          customizationProperties: comp.getFormattedCustomProperties(selectedWidget.customization_Properties || data.customization_Properties, selectedWidget.widgetChartType),
          quadrantText: data.quadrantText ? JSON.stringify(data.quadrantText) : null,
          metric: isBubbleChart ? [
            { metric_option: 'XAxis Metric', metric_group: '', metric_value: [uniqueMetrics[0]] },
            { metric_option: 'YAxis Metric', metric_group: '', metric_value: [uniqueMetrics[1]] },
            { metric_option: 'ZAxis Metric', metric_group: '', metric_value: [uniqueMetrics[2]] }
          ] : [{
            metric_option: 'Data',
            metric_group: '',
            metric_value: uniqueMetrics
          }],
          response_payload_trendline: data.response_payload_trendline,
          trendline_response: data.trendline_response,
          customization_Properties_trendline: data.customization_Properties_trendline,
          response_payload: data.response_payload
        };

        return builtData;
      } else {
        console.warn('[buildHistoricalWidgetData] ⚠️ opData is not a valid array or is empty.');
      }
    } catch (e) {
      console.error('[buildHistoricalWidgetData] ❌ Failed to build historical widget data:', e);
    }
    return data;
  }


  static getCustomData(comp: any, block: any): any {
    const config = comp.getSnapshotConfig(block);
    const resolvedCompareAcross = config?.Snapshot_Type || config?.snapshot_type || config?.snapshotType;

    if (block.messageId === comp.selectedMessageId && comp.selectedMessageId !== null) {
      if (comp.filteredWidgetData && Object.keys(comp.filteredWidgetData).length > 0) {
        if (resolvedCompareAcross) {
          comp.filteredWidgetData.snapshot_type = resolvedCompareAcross;
          comp.filteredWidgetData.selectedcompareacrossOption = resolvedCompareAcross;
        }
        return comp.filteredWidgetData;
      }
    }

    if (block.builtCustomData) {
      if (resolvedCompareAcross) {
        block.builtCustomData.snapshot_type = resolvedCompareAcross;
        block.builtCustomData.selectedcompareacrossOption = resolvedCompareAcross;
      }
      if (block.messageId === comp.selectedMessageId && !comp.filteredWidgetData) {
        comp.filteredWidgetData = block.builtCustomData;
        if (comp._configService) {
          comp._configService.allPageOutputData = [[block.builtCustomData]];
          comp._configService.allPageOutputDataWithoutTrend = [[block.builtCustomData]];
        }
      }
      return block.builtCustomData;
    }

    const analytical = block.analyticalData;

    if (!analytical) return block.fullAnswer?.GetCustomSnapshotWidget || {};

    let data = analytical.GetCustomSnapshotWidget;

    if (data && data.Data && typeof data.Data === 'string' && !data.widgetchart_name) {
      block.builtCustomData = ChatHistoryHelper.buildHistoricalWidgetData(comp, data, analytical);
      if (resolvedCompareAcross && block.builtCustomData) {
        block.builtCustomData.snapshot_type = resolvedCompareAcross;
        block.builtCustomData.selectedcompareacrossOption = resolvedCompareAcross;
      }
      return block.builtCustomData;
    }

    if (!data && analytical.getSnapshotOutput) {
      const snapshot = analytical.getSnapshotOutput;
      data = snapshot.response || snapshot;

      if (data && data.Data && typeof data.Data === 'string' && !data.widgetchart_name) {
        block.builtCustomData = ChatHistoryHelper.buildHistoricalWidgetData(comp, data, analytical);
        if (resolvedCompareAcross && block.builtCustomData) {
          block.builtCustomData.snapshot_type = resolvedCompareAcross;
          block.builtCustomData.selectedcompareacrossOption = resolvedCompareAcross;
        }
        return block.builtCustomData;
      }

      if (data && typeof data === 'object') {
        if (!data.metric) data.metric = [];
        if (!data.previewChartPostion) data.previewChartPostion = snapshot.Widget_ID || snapshot.widget_id || analytical.widget_id;
        if (!data.widgetchart_name) data.widgetchart_name = snapshot.widgetchart_name || snapshot.WidgetChartType || snapshot.widgetChartType;
        if (!data.widget_name) data.widget_name = snapshot.widget_name || snapshot.WidgetName;
        if (analytical.response_payload_trendline) data.response_payload_trendline = analytical.response_payload_trendline;
        if (analytical.trendline_response) data.trendline_response = analytical.trendline_response;
        if (analytical.customization_Properties_trendline) data.customization_Properties_trendline = analytical.customization_Properties_trendline;
        if (analytical.response_payload) data.response_payload = analytical.response_payload;

        if (!data.customizationProperties && snapshot.customization_Properties) {
          data.customizationProperties = snapshot.customization_Properties;
        }
      }
    }

    if (data && typeof data === 'object') {
      if (resolvedCompareAcross) {
        data.snapshot_type = resolvedCompareAcross;
        data.selectedcompareacrossOption = resolvedCompareAcross;
      }
    }

    return data || block.fullAnswer?.GetCustomSnapshotWidget || analytical || {};
  }

  static saveChatList(comp: any, title: string, chatResponse?: { [key: string]: string }[], questionAnswerMarkdown?: string, messageId?: string) {
    comp.isHistorySaving = true;
    comp.newChatId = null;
    const chatExists = comp.allChatList.some((chat: any) => chat.ChatId === comp.guidChatId);

    if (chatExists) {
      comp.newChatId = comp.guidChatId;
      comp.selectedChatId = comp.newChatId;
      comp.saveChatHistory(comp.newChatId, chatResponse, title, undefined, questionAnswerMarkdown, undefined, undefined, undefined, messageId);
      return;
    }

    comp.apiService.saveChatList(title, comp.guidChatId, comp.selected_client, comp.selected_study).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData && apiResponse.Data) {
          comp.newChatId = apiResponse.Data;
          comp.selectedChatId = comp.newChatId;
          comp.getChatListData();
          comp.saveChatHistory(comp.newChatId, chatResponse, title, undefined, questionAnswerMarkdown, undefined, undefined, undefined, messageId);
        } else {
          comp.isHistorySaving = false;
        }
      },
      error: () => {
        comp.isHistorySaving = false;
      }
    });
  }

  static loadChatDetails(comp: any, chatId: string, targetMessageId?: string, matchModule?: number) {
    const chat = comp.allChatList.find((c: any) => c.ChatId === chatId);
    if (chat && chat.ChatId) {
      comp.newChatId = chat.ChatId;
      comp.guidChatId = chat.ChatId;
      comp.selectedChatId = chat.ChatId;
      comp.currentView = 'chat';

      if (chat.ChatHistory && chat.ChatHistory.length > 0) {
        const sortedHistory = ChatHistoryHelper.sortChatHistoryByIndex(chat.ChatHistory);
        comp.chatPreviewBlocks = sortedHistory.map((msg: any) =>
          ChatHistoryHelper.createChatPreviewBlock(msg, comp.defaultQuestionAnswer, comp.DefaultWeight, comp.DefaultBase, comp.selectedWeight)
        );

        let targetMsg = null;
        let targetIndex = -1;
        if (targetMessageId) {
          targetIndex = sortedHistory.findIndex((m: any) => m.MessageId === targetMessageId);
          if (targetIndex > -1) {
            targetMsg = sortedHistory[targetIndex];

            if (matchModule) {
              const targetBlock = comp.chatPreviewBlocks[targetIndex];
              if (targetBlock) {
                const currentMcode = targetBlock.mcode || comp.mcode;
                if (currentMcode !== matchModule) {
                  const viewToSwitch = (targetBlock.availableViews || []).find((v: any) => v.mcode === matchModule);
                  if (viewToSwitch) {
                    setTimeout(() => {
                      comp.switchAnalysisView(viewToSwitch, targetBlock);
                    }, 100);
                  } else {
                    targetBlock.mcode = matchModule;
                  }
                }
              }
            }
          }
        }

        const messageToLoad = targetMsg || sortedHistory[sortedHistory.length - 1];

        if (messageToLoad && messageToLoad.MessageId) {
          comp.getChatResponse(messageToLoad.MessageId, true, true);

          if (targetMessageId) {
            if (targetIndex > 0) {
              comp.getChatResponse(sortedHistory[targetIndex - 1].MessageId, true, false);
            }
            if (targetIndex > -1 && targetIndex < sortedHistory.length - 1) {
              comp.getChatResponse(sortedHistory[targetIndex + 1].MessageId, true, false);
            }
          }
        } else {
          comp._loaderService.hideLoader();
        }
      }
    }
  }

  static confirmDelete(comp: any, chat: any) {
    chat.showDeleteConfirm = false;
    const isDeletingSelectedMessage = chat.messageId === comp.selectedMessageId;
    comp.apiService.deleteChatMessage(chat.messageId).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData) {
          const index = comp.chatPreviewBlocks.indexOf(chat);
          if (index > -1) {
            comp.chatPreviewBlocks.splice(index, 1);
          }
          if (isDeletingSelectedMessage || comp.chatPreviewBlocks.length === 0) {
            comp.selectedMessageId = null;
            comp.displayTable = false;
            comp.showSnapshot = false;
            comp.isCreateBtnClick = false;
            comp.CrossTabOutPutData = [];
            comp.OutputData = [];
            comp.clearOutputDataObject();
            comp.insightsVal = [];
            comp.insightsStreamingText = '';
            comp.insightsStreamingPoints = [];
            comp.isInsightspopupOpen = false;
            comp.setFailedCaseState(false);
            comp.isVerticalGroupBarChartClick = false;
            comp.isVerticalBarChartClick = false;
            comp.isVerticalStackChartClick = false;
            comp.isLineChartClick = false;

            if (comp.chatPreviewBlocks.length === 0) {
              setTimeout(() => {
                comp.navigateToLanding();
              }, 100);
            }
          }
          comp.showToast('Message deleted successfully', 'success');

          if (chat.isStarred && chat.messageId) {
            comp.apiService.updateChatStarred(chat.messageId, false).subscribe();
          }

          const beforeCount = comp.starredMessages.length;
          comp.starredMessages = comp.starredMessages.filter((m: any) => {
            const mId = (m.MessageId || (m as any).messageId || '').toString().toLowerCase();
            const targetId = (chat.messageId || '').toString().toLowerCase();
            return mId !== targetId;
          });
          comp.onStarredSearch();

          setTimeout(() => {
            comp.getChatListData();
            comp.loadStarredMessages();
          }, 300);
        } else {
          comp.showToast('Failed to delete message', 'error');
        }
      },
      error: (err: any) => {
        comp.showToast('Error while deleting message', 'error');
      }
    });
  }

  static confirmDeleteChatSession(comp: any): void {
    if (!comp.chatToDelete || !comp.chatToDelete.chatId || comp.isDeletingChat) return;

    comp.isDeletingChat = true;
    const chatId = comp.chatToDelete.chatId;

    comp.apiService.deleteChatSession(chatId).subscribe({
      next: (response: any) => {
        comp.isDeletingChat = false;
        try {
          const apiResponse = JSON.parse(response as string);
          if (apiResponse.HasValidData) {
            comp.allChatList = comp.allChatList.filter((c: any) => c.ChatId !== chatId);
            const searchText = comp.chatSearchText || '';
            const nestedSearchText = comp.chatNestedSearchText || '';
            const filteredChats = ChatHistoryHelper.filterChatsBySearch(comp.allChatList, searchText, nestedSearchText);
            comp.chatAccordions = ChatHistoryHelper.mapChatsToAccordions(filteredChats);

            if (comp.guidChatId === chatId || comp.selectedChatId === chatId) {
              comp.currentView = 'landing';
              comp.guidChatId = '';
              comp.selectedChatId = null;
              comp.chatPreviewBlocks = [];
            }

            comp.showToast('Chat deleted successfully', 'success');
          } else {
            comp.showToast(apiResponse.ExceptionMessage || 'Failed to delete chat', 'error');
          }
        } catch (e) {
          comp.showToast('Invalid response from server', 'error');
        }
        comp.chatToDelete = null;
      },
      error: (err: any) => {
        comp.isDeletingChat = false;
        comp.showToast('An error occurred while deleting the chat', 'error');
        comp.chatToDelete = null;
      }
    });
  }

  static saveChatHistory(comp: any,
    chatId: string,
    questionAnswer?: { [key: string]: string }[],
    question?: string,
    messageIndex?: number,
    questionAnswerMarkdown?: string,
    isLiked?: boolean | null,
    likeReason?: string,
    dislikeReason?: string,
    messageId?: string
  ) {
    comp.isHistorySaving = true;

    const questionText = question || '';
    const questionAnswerText = questionAnswerMarkdown || comp.defaultQuestionAnswer;

    let answerString: string;
    if (comp.allMemberResponses && Object.keys(comp.allMemberResponses).length > 0) {
      comp.allMemberResponses.final_mcode = comp.mcode;
      const safeStringify = (obj: any) => {
        const cache = new WeakSet();
        const ignoredKeys = ['Errors', 'Snapshot_Reason', 'BrandAnalysis_Reason', 'Crosstab_Reason'];
        return JSON.stringify(obj, (key, value) => {
          if (ignoredKeys.includes(key)) return undefined;
          if (typeof value === 'object' && value !== null) {
            if (cache.has(value)) return;
            cache.add(value);
          }
          return value;
        });
      };

      if (comp.allMemberResponses.brandanalysis && !comp.allMemberResponses.brand_analysis) {
        comp.allMemberResponses.brand_analysis = comp.allMemberResponses.brandanalysis;
        delete comp.allMemberResponses.brandanalysis;
      }

      const modules = [
        { code: 1, key: 'snapshot', esKey: 'getSnapshotOutput' },
        { code: 2, key: 'brand_analysis', esKey: 'getConfiguration' },
        { code: 3, key: 'crosstab', esKey: 'getConfiguration' }
      ];

      const innerMsgId = messageId || comp.selectedMessageId || 'current';

      modules.forEach((mod: any) => {
        if (comp.allMemberResponses[mod.key]) {
          // Use the raw analytical data from cache
          const cachedOpData = comp.multiViewDataCache[innerMsgId]?.[mod.code] || comp.multiViewDataCache['current']?.[mod.code];
          if (cachedOpData) {
            comp.allMemberResponses[mod.key][mod.esKey] = safeStringify(cachedOpData);
          }

          if (mod.code === 1 && comp.promptData) {
            comp.allMemberResponses[mod.key]['getSnapshotConfiguration'] = typeof comp.promptData === 'string' ? comp.promptData : JSON.stringify(comp.promptData);
          }

          if (mod.code === 1) {
            const widgetId = comp.promptData?.Widget_ID || comp.promptData?.widget_id || comp.widget_Id || comp.filteredWidgetID;
            const widgetName = comp.promptData?.Widget_Name || comp.promptData?.widget_name || comp.widget_Name;
            if (widgetId && !comp.allMemberResponses[mod.key].Widget_ID) comp.allMemberResponses[mod.key].Widget_ID = widgetId;
            if (widgetName && !comp.allMemberResponses[mod.key].Widget_Name) comp.allMemberResponses[mod.key].Widget_Name = widgetName;
          }

          // Backfill pipe strings so a later history restore can rebuild correct Level0/Level1 chips.
          if (mod.code === 2 && comp.lastBrandAnalysisOriginalPipes && !comp.allMemberResponses[mod.key].row && !comp.allMemberResponses[mod.key].Row) {
            comp.allMemberResponses[mod.key].row = comp.lastBrandAnalysisOriginalPipes.row || [];
            comp.allMemberResponses[mod.key].column = comp.lastBrandAnalysisOriginalPipes.column || [];
            comp.allMemberResponses[mod.key].row_nesting = comp.lastBrandAnalysisOriginalPipes.row_nesting || [];
            comp.allMemberResponses[mod.key].column_nesting = comp.lastBrandAnalysisOriginalPipes.column_nesting || [];
          }
          if (mod.code === 2 && comp.lastBrandAnalysisOriginalPipes && !comp.allMemberResponses[mod.key].filters) {
            comp.allMemberResponses[mod.key].filters = comp.lastBrandAnalysisOriginalPipes.filters || {};
          }

          const insightsCache = comp.multiViewInsightsCache[innerMsgId] || comp.multiViewInsightsCache['current'];
          if (insightsCache && insightsCache[mod.code]) {
            const cachedData = insightsCache[mod.code];
            if (mod.code === 1) {
              comp.allMemberResponses[mod.key]['getSnapshotInsights'] = cachedData.standard_insights || '';
              comp.allMemberResponses[mod.key]['getSnapshotInsights_Trend'] = cachedData.trend_insights || '';
            } else {
              comp.allMemberResponses[mod.key]['getInsights'] = cachedData.rawInsights || '';
            }
            comp.allMemberResponses[mod.key]['getQnA'] = cachedData.qnaText || '';
          }
        }
      });

      answerString = safeStringify(comp.allMemberResponses);
    } else {
      answerString = questionAnswer && questionAnswer.length > 0
        ? JSON.stringify(questionAnswer)
        : JSON.stringify([]);
    }

    const innerMsgIdGlobal = messageId || comp.selectedMessageId || 'current';
    let aggregatedInsights: string[] = [];
    const insightsCacheGlobal = comp.multiViewInsightsCache[innerMsgIdGlobal] || comp.multiViewInsightsCache['current'];
    if (insightsCacheGlobal) {
      Object.values(insightsCacheGlobal).forEach((cachedData: any) => {
        if (cachedData.rawInsights) aggregatedInsights.push(cachedData.rawInsights);
        if (cachedData.standard_insights) aggregatedInsights.push(cachedData.standard_insights);
        if (cachedData.trend_insights) aggregatedInsights.push(cachedData.trend_insights);
      });
    }

    let finalMessageIndex = messageIndex;
    if (finalMessageIndex === undefined || finalMessageIndex === null) {
      const idx = messageId ? comp.chatPreviewBlocks.findIndex((b: any) => b.messageId === messageId) : -1;
      finalMessageIndex = idx > -1 ? idx : (comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks.length - 1 : 0);
    }

    const chatHistory: ChatHistoryDocument = {
      UserId: comp.userId || '',
      ChatId: chatId || '',
      MessageId: messageId,
      Question: questionText,
      MessageIndex: finalMessageIndex,
      QuestionAnswer: questionAnswerText,
      IsActive: true,
      IsStarred: false,
      Insights: aggregatedInsights.length > 0 ? aggregatedInsights.join('\n\n') : undefined,
      IsLiked: isLiked ?? null,
      LikeReason: likeReason || '',
      DislikeReason: dislikeReason || ''
    };

    const chatAnswer: ChatAnswerDocument = {
      UserId: comp.userId || '',
      ChatId: chatId || '',
      Answer: answerString
    };

    const diagnosticPayload = comp.prepareDiagnosticPayload();

    const request: SaveChatResponseRequest = {
      ChatHistory: chatHistory,
      ChatAnswer: chatAnswer,
      PythonPrompt: diagnosticPayload ? JSON.stringify(diagnosticPayload) : undefined,
      SelectedClient: comp.selected_client || comp.landingdata?.client_code || undefined,
      SelectedStudy: comp.selected_study || comp.landingdata?.study || undefined,
      InputQuery: questionText || undefined
    };
    comp.apiService.saveChatHistory(request).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData && apiResponse.Data) {
          const returnedMessageId = apiResponse.Data;
          if (comp.chatPreviewBlocks.length > 0) {
            let targetChat = null;
            if (messageId) {
              targetChat = comp.chatPreviewBlocks.find((b: any) => b.messageId === messageId);
            }
            if (!targetChat) {
              targetChat = comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1];
            }

            if (targetChat && (!targetChat.messageId || targetChat.isTemp)) {
              targetChat.messageId = returnedMessageId;
              targetChat.isTemp = false;
              comp.selectedMessageId = returnedMessageId;
              comp.updateChartDisablingFlags(targetChat);
              comp.cdr.detectChanges();
              comp.scrollToMessage(returnedMessageId, 'start');
            }

            if (comp.isWaitingForFinalSave) {
              if (document.hidden) {
                comp.unreadNotificationCount++;
                comp.playNotificationSound();

                const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
                if (link) {
                  if (!comp.baseFaviconHref) {
                    comp.baseFaviconHref = link.href;
                  }

                  const canvas = document.createElement('canvas');
                  canvas.width = 32;
                  canvas.height = 32;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                      ctx.drawImage(img, 0, 0, 32, 32);

                      // Draw red notification badge
                      ctx.beginPath();
                      ctx.arc(23, 23, 9, 0, 2 * Math.PI);
                      ctx.fillStyle = '#ff3b30';
                      ctx.fill();

                      // Add number inside badge
                      const displayCount = comp.unreadNotificationCount > 9 ? '9+' : comp.unreadNotificationCount.toString();
                      ctx.fillStyle = '#ffffff';
                      ctx.font = 'bold 12px Arial';
                      if (displayCount === '9+') ctx.font = 'bold 10px Arial';
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'middle';
                      ctx.fillText(displayCount, 23, 24);

                      link.href = canvas.toDataURL('image/png');
                    };
                    img.src = comp.baseFaviconHref;
                  }
                }

                const onVisibilityChange = () => {
                  if (!document.hidden) {
                    if (comp.unreadNotificationCount > 0) {
                      const msg = comp.unreadNotificationCount > 1 ? `${comp.unreadNotificationCount} answers generated and saved.` : "Answer generated and saved.";
                      comp.showToast(msg, 'success');
                      comp.unreadNotificationCount = 0;
                      if (link && comp.baseFaviconHref) {
                        link.href = comp.baseFaviconHref;
                        comp.baseFaviconHref = '';
                      }
                    }
                    document.removeEventListener('visibilitychange', onVisibilityChange);
                  }
                };
                document.addEventListener('visibilitychange', onVisibilityChange);
              } else {
                comp.showToast("Answer generated and saved.", 'success');
              }

              comp.isWaitingForFinalSave = false;

              if (comp.isProcessingQueue) {
                setTimeout(() => {
                  comp.processNextQueuedQuestion();
                }, 1000);
              }
            }
          }
          comp.getChatListData();
        }
        comp.isHistorySaving = false;
      },
      error: (err: any) => {
        comp.isHistorySaving = false;
      }
    });
  }

  static onDeleteClick(comp: any, chat: any) {
    if (!chat.messageId) {
      comp.showToast('Cannot delete: Message ID is not available', 'error');
      return;
    }

    comp.chatPreviewBlocks.forEach((c: any) => {
      if (c !== chat) {
        c.showDeleteConfirm = false;
        c.showLikeInput = false;
        c.showDislikeInput = false;
      }
    });

    chat.showDeleteConfirm = !chat.showDeleteConfirm;

    if (chat.showDeleteConfirm) {
      chat.showLikeInput = false;
      chat.showDislikeInput = false;
    }
  }

  static getPostStreamMessage(comp: any): string {
    switch (comp.mcode) {
      case 1: return 'Plotting Snapshot Visualization';
      case 2: return 'Building Brand Analysis Table';
      case 3: return 'Generating Crosstab Table';
      default: return 'Finalizing data analysis';
    }
  }

  static getCurrentQnAResponse(comp: any, block?: any): string {
    const targetChat = block || (comp.selectedMessageId
      ? comp.chatPreviewBlocks.find((chat: any) => chat.messageId === comp.selectedMessageId)
      : comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1]);

    const msgId = targetChat?.messageId || 'current';
    const currentMcode = targetChat?.mcode || comp.mcode;

    if (comp.multiViewInsightsCache[msgId]?.[currentMcode]?.qnaText) {
      return comp.multiViewInsightsCache[msgId][currentMcode].qnaText;
    }

    const response = targetChat?.QuestionAnswer || targetChat?.response || '';

    if (!block || block.messageId === comp.selectedMessageId) {
      if ((!response || response === 'Answering question...' || response === 'No data Available') && comp.insightsVal && Array.isArray(comp.insightsVal) && comp.insightsVal.length > 0) {
        return comp.insightsVal.join('\n');
      }
    }

    return response;
  }

  static updateQuestionAnswerForSelectedMessage(comp: any, questionAnswerText: string): void {
    if (comp.chatPreviewBlocks.length === 0) {
      return;
    }

    const targetChat = comp.selectedMessageId
      ? comp.chatPreviewBlocks.find((chat: any) => chat.messageId === comp.selectedMessageId)
      : comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1];

    if (targetChat) {
      targetChat.QuestionAnswer = questionAnswerText;
    }
  }

  static onNewChatClick(comp: any): void {
    comp.abandoningSessionId = comp.guidChatId;
    comp.isNewChatStarted = true;
    comp.guidChatId = '';
    comp.validatedSessionId = null;
    comp.selectedChatId = null;
    comp.inputValue = '';
    comp.chatPreviewBlocks = [];
    comp.insightsVal = '';
    comp.displayTable = false;
    comp.showSnapshot = false;
    comp.setFailedCaseState(false);
    comp.isInsightspopupOpen = false;
    comp.OutputdataObject.Row = [];
    comp.OutputdataObject.Column = [];
    comp.CrossTabOutPutData = [];
    comp.askMeLanding = true;
  }

  static usageDataLog(comp: any, feature: string) {
    let summarytext = '';
    if (comp.summaryText != undefined && comp.summaryText != null)
      summarytext = comp.summaryText?.nativeElement.innerText;
    else summarytext = comp.selectionSummary.replace(/<[^>]*>/g, '');
    comp._usermanagementService.UsageData(
      COMMON_CONSTANTS.DataExplorerModuleId.toString(),
      comp.selected_client,
      comp.selected_study,
      feature,
      summarytext,
      ''
    );
  }

  static configChanges(comp: any, data: any, study: any) {
    let comparisionpoint;
    let confidenceLevel;
    let weights;
    let dataFormat;
    data = data.filter((a: any) => a.study === study);
    if (data.length > 0) {
      if (data[0].comparison_point.length > 0) {
        const isNoneKeyPresent = data[0].comparison_point.some(
          (itemConst: any) => itemConst.key === 'None'
        );
        if (!isNoneKeyPresent) {
          data[0].comparison_point.unshift({ key: 'None', value: true });
        }
        comparisionpoint = data[0].comparison_point.filter(
          (a: any) => a.value
        );
      }

      if (data[0].confidence_level.length > 0) {
        confidenceLevel = data[0].confidence_level.filter(
          (a: any) => a.value
        );
      }

      if (data[0].weight.length > 0) {
        weights = data[0].weight.filter((a: any) => a.is_selected);
      }

      if (data[0].dataformat.length > 0) {
        dataFormat = data[0].dataformat.filter((a: any) => a.value);
      }
      comp.aggregatedVariables = data[0].aggregatedvariables;
    }
  }

  static onSendMessage(comp: any): void {
    if (comp.arktabaccess) {
      console.warn('[SEND GUARD] Cannot send new message - previous question still processing');
      return;
    }

    if (comp.searchText && comp.searchText.trim()) {
      comp.inputValue = comp.searchText.trim();
      comp.isNewChatSession = false;
      comp.selectedMessageId = null;
      comp.sendInputMessage();
      comp.searchText = '';
    }
  }

  static async createNewUserIdentity(comp: any): Promise<void> {
    try {
      const userDoc: UserIdentityDocument = {
        UserId: '',
      };

      const response = await firstValueFrom(comp.apiService.saveUserIdentity(userDoc));
      const apiResponse = JSON.parse(response as string);

      if (apiResponse.HasValidData && apiResponse.Data) {
        comp.userId = apiResponse.Data;
        sessionStorage.setItem('arkUserId', comp.userId);
      } else {
        console.error('[User Identity] API responded with invalid data when creating new user.');
      }
    } catch (error) {
      console.error('[User Identity] Critical Error creating new user identity:', error);
    }
  }

  static preFetchMetadata(comp: any): Promise<void> {
    if (comp.metadataFetchPromise) return comp.metadataFetchPromise;

    const client = comp.selected_client || '';
    const study = comp.selected_study || '';

    if (client && study) {
      comp.metadataFetchPromise = new Promise<void>((resolve) => {
        comp._configService.getGeneralConfigData({
          study: study,
          client_name: client,
          client_code: client,
          isBrandAnalysis: true
        }).subscribe({
          next: (res: any) => {
            if (res) {
              comp.handleMetadataPreFetchResponse(res);
            }
            resolve();
          },
          error: (err: any) => {
            console.error('[ACCESS-CHECK] Metadata Pre-fetch Error:', err);
            resolve();
          }
        });
      });
      return comp.metadataFetchPromise;
    }
    return Promise.resolve();
  }
}
